import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Loader2, Save, Workflow, Lock } from "lucide-react";
import { useData } from "../contexts/DataContext";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Hardcoded stage transitions that are baked into the backend and can't be
// edited from this page. Surfaced here so admins can see the full picture
// of what fires on their pipeline without hunting through code.
const SYSTEM_RULES = [
  {
    trigger: "Quote fully countersigned (deposit needed)",
    action: "Project → Approved / Deposit Requested",
    detail: "Fires as soon as both customer + admin signatures are on the quote AND a deposit is required. Also emails the customer the deposit invoice.",
  },
  {
    trigger: "Quote fully countersigned (no deposit)",
    action: "Project → Scheduling",
    detail: "Same signing event, but when the template has no deposit configured, the project skips straight to Scheduling.",
  },
  {
    trigger: "Any payment received",
    action: "Customer receipt + team notification email",
    detail: "Every payment (partial or full, Stripe or manual) sends a receipt to the customer AND emails operations@tidesteam.com so the team never misses money coming in.",
  },
];

const TRIGGER_LABELS = {
  event_created: {
    title: "Any event scheduled",
    description: "Fires when any event/appointment is scheduled on a project. Use this to auto-move projects to your \"Event Scheduled\" column so appointment-reminder emails can fire.",
  },
  quote_signed_fully: {
    title: "Quote fully signed (customer + you)",
    description: "Both signatures present on a quote.",
  },
  invoice_sent: {
    title: "Any invoice sent",
    description: "Fires for every invoice send — deposit, final, or plain. Use this if you want ALL invoices to move the project.",
  },
  deposit_invoice_sent: {
    title: "Deposit invoice sent",
    description: "A deposit invoice was created and sent to the customer.",
  },
  final_invoice_sent: {
    title: "Final invoice sent",
    description: "A balance / final invoice was created and sent.",
  },
  deposit_paid: {
    title: "Deposit paid",
    description: "Fires when every deposit invoice on the project is fully paid AND the project is still sitting at Approved / Deposit. Default: Scheduling. Won't demote projects already past Approved / Deposit.",
  },
  all_invoices_paid: {
    title: "All final invoices paid",
    description: "Fires once every non-deposit (balance / final / progress / custom) invoice on the project is fully paid. Default: Final Paid / Payroll Owed. Never drags a project back from Paid & Closed.",
  },
};

export default function StageAutomations() {
  const { pipelineStages, pipelineCategories, fetchPipelineStages, fetchPipelineCategories } = useData();
  const [rules, setRules] = useState({});
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPipelineStages();
    fetchPipelineCategories();
    load();
  }, [fetchPipelineStages, fetchPipelineCategories]);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/stage-automations`);
      setRules(res.data.rules || {});
      setTriggers(res.data.triggers || []);
    } catch {
      toast.error("Failed to load stage automations");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/stage-automations`, { rules });
      setRules(res.data.rules);
      toast.success("Stage automations saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const setRule = (trigger, key, value, service) => {
    setRules(prev => {
      const cur = prev[trigger] || { default: "", by_service: {} };
      if (key === "default") return { ...prev, [trigger]: { ...cur, default: value } };
      const by = { ...(cur.by_service || {}) };
      if (value) by[service] = value;
      else delete by[service];
      return { ...prev, [trigger]: { ...cur, by_service: by } };
    });
  };

  const stagesForCategory = (categoryValue) =>
    pipelineStages
      .filter(s => !categoryValue || s.category === categoryValue)
      .sort((a, b) => (a.order || 0) - (b.order || 0));

  const serviceCategories = pipelineCategories
    .filter(c => !["production", "billing"].includes(c.value));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F4C81]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header title="Stage Automations">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270]"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-stage-automations-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </Header>

      <div className="p-3 sm:p-6 space-y-4 max-w-4xl">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 flex gap-2">
          <Workflow className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">How it works</p>
            <p className="mt-1">
              For each trigger below, set a default destination stage. Optionally override per service type
              (Roofing, Fencing, etc). When a project hits the trigger, it auto-moves to that stage.
              Leave blank to skip.
            </p>
          </div>
        </div>

        {/* Read-only view of hardcoded system rules that don't live in this table */}
        <Card className="border-slate-200 bg-slate-50/60" data-testid="system-rules-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-slate-500" />
              Built-in system rules
            </CardTitle>
            <CardDescription className="text-xs">
              These always run in the background — they're wired to money/signature events, not stages. Listed here so you can see the full picture. Not editable from this screen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-slate-200 border border-slate-200 rounded-lg overflow-hidden bg-white" data-testid="system-rules-list">
              {SYSTEM_RULES.map((r, i) => (
                <div key={i} className="px-3.5 py-2.5" data-testid={`system-rule-${i}`}>
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-slate-800 min-w-0 flex-1">{r.trigger}</span>
                    <span className="text-slate-400 text-xs">→</span>
                    <span className="text-xs font-medium text-[#0F4C81] text-right">{r.action}</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{r.detail}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {triggers.map(trigger => {
          const rule = rules[trigger] || { default: "", by_service: {} };
          const meta = TRIGGER_LABELS[trigger] || { title: trigger, description: "" };
          return (
            <Card key={trigger} data-testid={`automation-card-${trigger}`}>
              <CardHeader>
                <CardTitle className="text-base">{meta.title}</CardTitle>
                <CardDescription className="text-xs">{meta.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Default */}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-xs font-medium text-slate-700">Default (any service)</p>
                    <p className="text-[11px] text-slate-400">Used when no service-specific rule below matches.</p>
                  </div>
                  <select
                    value={rule.default || ""}
                    onChange={(e) => setRule(trigger, "default", e.target.value)}
                    className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs min-w-[220px]"
                    data-testid={`default-stage-${trigger}`}
                  >
                    <option value="">— Skip —</option>
                    {stagesForCategory().map(s => (
                      <option key={s.value} value={s.value}>
                        [{pipelineCategories.find(c => c.value === s.category)?.name || s.category}] {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Per-service overrides */}
                <p className="text-[11px] uppercase tracking-wide text-slate-400 pt-1">Per-Service Override (optional)</p>
                {serviceCategories.map(cat => (
                  <div key={cat.value} className="flex items-center gap-3">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color || "#0F4C81" }}
                    />
                    <p className="text-xs text-slate-700 flex-1">{cat.name}</p>
                    <select
                      value={rule.by_service?.[cat.value] || ""}
                      onChange={(e) => setRule(trigger, "by_service", e.target.value, cat.value)}
                      className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs min-w-[220px]"
                      data-testid={`service-stage-${trigger}-${cat.value}`}
                    >
                      <option value="">— Use default —</option>
                      {stagesForCategory(cat.value).map(s => (
                        <option key={s.value} value={s.value}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
